import React, { useState, useEffect } from 'react';
import { Search, RefreshCw, ChevronLeft, ChevronRight, Calendar, Mail, Filter, Clock, X } from 'lucide-react';

// Sample data generator for demonstration purposes
const generateSampleEmails = (count = 100) => {
  const services = ['Notification Service', 'Authentication', 'User Onboarding', 'Marketing', 'Support', 'Billing', 'Transactional', 'Newsletter'];
  const titles = [
    'Welcome to our platform!',
    'Your account has been created',
    'Password reset request',
    'Security alert: new login',
    'Subscription renewal',
    'Weekly newsletter',
    'Your invoice is ready',
    'Action required: verify your email',
    'Thank you for your purchase',
    'Your support ticket has been updated',
    'Important account notification',
    'Monthly report available',
    'Product update announcement',
    'Payment confirmation',
    'Account status update'
  ];

  const domains = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'company.com', 'example.org', 'mail.io', 'protonmail.com'];

  // Generate emails for the past 30 days
  const now = Date.now();
  const thirtyDaysAgo = now - (30 * 24 * 60 * 60 * 1000);

  return Array.from({ length: count }, (_, i) => {
    // Random time in the past 30 days
    const sentAt = thirtyDaysAgo + Math.random() * (now - thirtyDaysAgo);

    // Generate a random email address
    const firstName = ['john', 'jane', 'alex', 'sara', 'michael', 'emma', 'david', 'olivia'][Math.floor(Math.random() * 8)];
    const lastName = ['smith', 'doe', 'johnson', 'brown', 'wilson', 'taylor', 'miller', 'davis'][Math.floor(Math.random() * 8)];
    const domain = domains[Math.floor(Math.random() * domains.length)];
    const separator = Math.random() > 0.5 ? '.' : '';
    const userEmail = `${firstName}${separator}${lastName}@${domain}`;

    return {
      id: i + 1,
      sentAt,
      title: titles[Math.floor(Math.random() * titles.length)],
      userEmail,
      service: services[Math.floor(Math.random() * services.length)]
    };
  });
};

// Format date for display
const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

// Format relative time (e.g. "2 hours ago")
const formatRelativeTime = (timestamp) => {
  const now = Date.now();
  const diffMs = now - timestamp;

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else {
    return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
  }
};

// Service badge component
const ServiceBadge = ({ service }) => {
  // Map services to colors
  const colorMap = {
    'Notification Service': 'bg-blue-100 text-blue-800',
    'Authentication': 'bg-green-100 text-green-800',
    'User Onboarding': 'bg-purple-100 text-purple-800',
    'Marketing': 'bg-yellow-100 text-yellow-800',
    'Support': 'bg-indigo-100 text-indigo-800',
    'Billing': 'bg-red-100 text-red-800',
    'Transactional': 'bg-teal-100 text-teal-800',
    'Newsletter': 'bg-orange-100 text-orange-800'
  };

  const color = colorMap[service] || 'bg-gray-100 text-gray-800';

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>
      {service}
    </span>
  );
};

const EmailDashboard = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterService, setFilterService] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('sentAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [dateRange, setDateRange] = useState({
    start: null,
    end: null
  });
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    services: {}
  });

  // Fetch data (simulated for this example)
  const fetchData = () => {
    setLoading(true);

    // Simulate API call with setTimeout
    setTimeout(() => {
      const newData = generateSampleEmails(250);
      setEmails(newData);
      setLoading(false);
      setLastRefreshed(new Date());

      // Calculate stats
      const now = Date.now();
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);

      const emailsToday = newData.filter(email => email.sentAt >= startOfToday.getTime());

      const serviceCount = newData.reduce((acc, email) => {
        acc[email.service] = (acc[email.service] || 0) + 1;
        return acc;
      }, {});

      setStats({
        total: newData.length,
        today: emailsToday.length,
        services: serviceCount
      });
    }, 800);
  };

  // Initial data load
  useEffect(() => {
    fetchData();
  }, []);

  // Auto-refresh timer
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [refreshInterval]);

  // Handle manual refresh
  const handleRefresh = () => {
    fetchData();
  };

  // Sort function for emails
  const sortEmails = (a, b) => {
    if (sortBy === 'sentAt') {
      return sortOrder === 'asc'
        ? a.sentAt - b.sentAt
        : b.sentAt - a.sentAt;
    } else if (sortBy === 'title') {
      return sortOrder === 'asc'
        ? a.title.localeCompare(b.title)
        : b.title.localeCompare(a.title);
    } else if (sortBy === 'userEmail') {
      return sortOrder === 'asc'
        ? a.userEmail.localeCompare(b.userEmail)
        : b.userEmail.localeCompare(a.userEmail);
    } else if (sortBy === 'service') {
      return sortOrder === 'asc'
        ? a.service.localeCompare(b.service)
        : b.service.localeCompare(a.service);
    }
    return 0;
  };

  // Handle sort change
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Filter emails based on search term, service filter, and date range
  const filteredEmails = emails.filter(email => {
    const matchesSearch = searchTerm === '' ||
      email.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.service.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesService = filterService === '' || email.service === filterService;

    const matchesDateRange = (dateRange.start === null || email.sentAt >= dateRange.start) &&
      (dateRange.end === null || email.sentAt <= dateRange.end);

    return matchesSearch && matchesService && matchesDateRange;
  });

  // Sort the filtered emails
  const sortedEmails = [...filteredEmails].sort(sortEmails);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEmails = sortedEmails.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedEmails.length / itemsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber < 1) pageNumber = 1;
    if (pageNumber > totalPages) pageNumber = totalPages;
    setCurrentPage(pageNumber);
  };

  // Service filter options
  const serviceOptions = [...new Set(emails.map(email => email.service))].sort();

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setFilterService('');
    setDateRange({ start: null, end: null });
    setCurrentPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters = searchTerm !== '' || filterService !== '' || dateRange.start !== null || dateRange.end !== null;

  // Create a date filter for the last 24 hours
  const filterLast24Hours = () => {
    const now = Date.now();
    const twentyFourHoursAgo = now - (24 * 60 * 60 * 1000);
    setDateRange({
      start: twentyFourHoursAgo,
      end: now
    });
    setCurrentPage(1);
  };

  // Create a date filter for the last 7 days
  const filterLastWeek = () => {
    const now = Date.now();
    const sevenDaysAgo = now - (7 * 24 * 60 * 60 * 1000);
    setDateRange({
      start: sevenDaysAgo,
      end: now
    });
    setCurrentPage(1);
  };

  // Refresh interval options
  const refreshOptions = [
    { value: 10, label: '10 seconds' },
    { value: 30, label: '30 seconds' },
    { value: 60, label: '1 minute' },
    { value: 300, label: '5 minutes' },
  ];

  return (
    <div className="w-full max-w-6xl mx-auto bg-white rounded-lg shadow">
      {/* Dashboard Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Email Monitoring Dashboard (MOCK DATA)</h1>
            <p className="text-sm text-gray-500 mt-1">
              Last refreshed: {formatDate(lastRefreshed)} ({formatRelativeTime(lastRefreshed.getTime())})
            </p>
          </div>
          <div className="flex items-center space-x-2 mt-4 md:mt-0">
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm"
            >
              {refreshOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button
              onClick={handleRefresh}
              className="flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm"
            >
              <RefreshCw size={16} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 border-b border-gray-200">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 font-medium">Total Emails</p>
              <h3 className="text-3xl font-bold text-gray-800">{stats.total}</h3>
            </div>
            <Mail className="h-10 w-10 text-blue-500" />
          </div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-600 font-medium">Sent Today</p>
              <h3 className="text-3xl font-bold text-gray-800">{stats.today}</h3>
            </div>
            <Calendar className="h-10 w-10 text-green-500" />
          </div>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 font-medium">Services Used</p>
              <h3 className="text-3xl font-bold text-gray-800">{Object.keys(stats.services).length}</h3>
            </div>
            <Filter className="h-10 w-10 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col md:flex-row justify-between mb-4 space-y-4 md:space-y-0">
          {/* Search */}
          <div className="relative max-w-xs">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={16} className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search emails..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="flex flex-wrap gap-3">
            {/* Service Filter */}
            <select
              value={filterService}
              onChange={(e) => {
                setFilterService(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All Services</option>
              {serviceOptions.map((service) => (
                <option key={service} value={service}>{service}</option>
              ))}
            </select>

            {/* Time Range Buttons */}
            <button
              onClick={filterLast24Hours}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center"
            >
              <Clock size={16} className="mr-1" />
              Last 24 Hours
            </button>

            <button
              onClick={filterLastWeek}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center"
            >
              <Calendar size={16} className="mr-1" />
              Last 7 Days
            </button>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-md hover:bg-red-100 flex items-center"
              >
                <X size={16} className="mr-1" />
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mt-2">
            {searchTerm && (
              <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm flex items-center">
                Search: {searchTerm}
                <button onClick={() => setSearchTerm('')} className="ml-2">
                  <X size={14} />
                </button>
              </div>
            )}

            {filterService && (
              <div className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm flex items-center">
                Service: {filterService}
                <button onClick={() => setFilterService('')} className="ml-2">
                  <X size={14} />
                </button>
              </div>
            )}

            {dateRange.start && (
              <div className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm flex items-center">
                Date: {formatDate(dateRange.start)} to {dateRange.end ? formatDate(dateRange.end) : 'now'}
                <button onClick={() => setDateRange({ start: null, end: null })} className="ml-2">
                  <X size={14} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Items Per Page Selector */}
      <div className="p-4 border-b border-gray-200 flex justify-end">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Items per page:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="px-2 py-1 border border-gray-300 rounded-md text-sm"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('sentAt')}
              >
                <div className="flex items-center">
                  Sent At
                  {sortBy === 'sentAt' && (
                    <span className="ml-1">
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('title')}
              >
                <div className="flex items-center">
                  Title
                  {sortBy === 'title' && (
                    <span className="ml-1">
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('userEmail')}
              >
                <div className="flex items-center">
                  Recipient
                  {sortBy === 'userEmail' && (
                    <span className="ml-1">
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('service')}
              >
                <div className="flex items-center">
                  Service
                  {sortBy === 'service' && (
                    <span className="ml-1">
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                  Loading emails...
                </td>
              </tr>
            ) : currentEmails.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                  No emails found
                </td>
              </tr>
            ) : (
              currentEmails.map((email) => (
                <tr key={email.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(email.sentAt)}</div>
                    <div className="text-xs text-gray-500">{formatRelativeTime(email.sentAt)}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{email.title}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">{email.userEmail}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ServiceBadge service={email.service} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && sortedEmails.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedEmails.length)} of {sortedEmails.length} emails
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded ${currentPage === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              <ChevronLeft size={16} />
            </button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Show at most 5 page buttons, centered around current page
                let pageToShow;
                if (totalPages <= 5) {
                  pageToShow = i + 1;
                } else if (currentPage <= 3) {
                  pageToShow = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageToShow = totalPages - 4 + i;
                } else {
                  pageToShow = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageToShow}
                    onClick={() => paginate(pageToShow)}
                    className={`w-8 h-8 flex items-center justify-center rounded ${currentPage === pageToShow
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {pageToShow}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`px-3 py-1 rounded ${currentPage === totalPages || totalPages === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailDashboard;